import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Col, Alert } from 'react-bootstrap';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormValidator } from '../../../helpers/form-validator';
import { Validator, Icon, Heading5, TitleIcon } from '../../widgets';
import { isValidEmail, parseJSON } from '../../../helpers/common';
import { createUser, editUser } from '../../../actions/users';
import { QAPP_ROLE_ID } from '../../../constants';

const getRules = userDetail => {
    const isPasswordMatched = value => userDetail.password !== value;
    const isPasswordRequired = () => userDetail.userId !== null && userDetail.password === null &&
        userDetail.confirmPassword === null;
    const isJSONData = value => {
        if (!value) return true;
        const parsedData = parseJSON(value);
        if (parsedData && parsedData !== null) return true;
        else return false;
    };

    const rules = [
        {
            field: 'loginId',
            method: 'isEmpty',
            validWhen: false,
            message: 'Username is required.',
        },
        {
            field: 'firstName',
            method: 'isEmpty',
            validWhen: false,
            message: 'First name is required.',
        },
        {
            field: 'roleId',
            method: 'isInt',
            validWhen: true,
            message: 'Role is required'
        },
        {
            field: 'statusId',
            method: 'isInt',
            validWhen: true,
            message: 'Status is required'
        },
        {
            field: 'password',
            method: 'isEmpty',
            validWhen: isPasswordRequired(),
            message: 'Password is required.',
        },
        {
            field: 'confirmPassword',
            method: 'isEmpty',
            validWhen: isPasswordRequired(),
            message: 'Confirm Password is required.',
        },
        {
            field: 'confirmPassword',
            method: isPasswordMatched,
            validWhen: isPasswordRequired(),
            message: "Confirm password doesn't match with password.",
        },
        {
            field: 'data',
            method: isJSONData,
            validWhen: true,
            message: 'Enter valid JSON data.',
        },
    ]

    return rules;
}

const UserForm = props => {
    const { actions, userId, roleList, statusList, userInfo, onHide } = props;
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const defaultValues = {
        userId: null,
        loginId: null,
        firstName: null,
        lastName: null,
        roleId: 3,
        statusId: 1,
        password: null,
        confirmPassword: null,
        email: null,
        data: null,
        assignedFarmIds: []
    };
    const [userDetail, setUserDetail] = useState(defaultValues);

    const isCreateMode = userId ? false : true;
    const validator = new FormValidator(getRules(userDetail));
    const [validationInfo, setValidationInfo] = useState({ validation: validator.valid(), submitted: false });

    useEffect(() => {
        const isJSONData = value => {
            if (!value) return false;
            const parsedData = parseJSON(value);
            if (parsedData && parsedData !== null) return true;
            else return false;
        };

        if (!isCreateMode)
            setUserDetail({
                ...userDetail,
                userId: userInfo.user_id,
                loginId: userInfo.login,
                firstName: userInfo.first_name,
                lastName: userInfo.last_name,
                roleId: userInfo.role_id ? userInfo.role_id : 3,
                statusId: userInfo.status_id ? userInfo.status_id : 1,
                email: userInfo.email,
                data: isJSONData(userInfo.meta_data) ? JSON.stringify(JSON.parse(userInfo.meta_data), undefined, 4) : userInfo.meta_data,
                assignedFarmIds: userInfo.assignedFarmIds ? userInfo.assignedFarmIds : []
            });
    }, []);

    const handleChange = e => {
        const { name, value } = e.target;
        setUserDetail({ ...userDetail, [name]: value.length > 0 ? value : null });
    };

    const handleSelect = (selectedOption, target) => {
        setUserDetail({ ...userDetail, [target.name]: selectedOption ? selectedOption.value : "" });
    };

    const handleCancel = () => onHide();

    const handleSave = e => {
        setErrorMessage(null);
        e.preventDefault();
        const validation = validator.validate(userDetail);
        setValidationInfo({ validation, submitted: true });

        const params = {
            login: userDetail.loginId,
            first_name: userDetail.firstName,
            last_name: userDetail.lastName ? userDetail.lastName : "",
            email: (userDetail.email || userDetail.loginId ? isValidEmail(userDetail.loginId) ? userDetail.loginId : null : null),
            role_id: userDetail.roleId,
            status_id: userDetail.statusId,
            meta_data: JSON.parse(userDetail.data),
        }

        if (validation.isValid) {
            const cb = (isSuccess, error) => {
                setIsSaving(false);
                if (isSuccess) onHide();
                else setErrorMessage(error);
            }

            if (isCreateMode) {
                params.password = userDetail.password;
                actions.createUser(params, cb);
                setIsSaving(true);
            } else {
                params.user_id = userDetail.userId;
                if (userDetail.password) params.password = userDetail.password;
                actions.editUser(params, cb);
                setIsSaving(true);
            }
        }
    };

    const Validation = validationInfo.submitted ? validator.validate(userDetail) : validationInfo.validation;
    const isPasswordRequired = () => !isCreateMode && userDetail.password === null &&
        userDetail.confirmPassword === null;

    return <Modal show={true} backdrop="static" onHide={onHide} size='md'>
        <Modal.Header closeButton style={{ color: '#064d9e' }}>
            <Modal.Title >
                <Heading5>
                    <TitleIcon className="fa fa-user" />
                    {isCreateMode ? 'Create new user' : 'Edit user'}
                </Heading5>
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {errorMessage && <div className="user-form-alert">
                <Alert show={true} variant="danger" onClose={() => setErrorMessage(null)} dismissible>{errorMessage}</Alert>
            </div>}
            <Form autoComplete='off'>
                <Form.Group>
                    <Validator validation={Validation.loginId}>
                        <Form.Label >Username<Validator.Mand /></Form.Label>
                        <Form.Control
                            size='sm' type="string" placeholder='Username'
                            name='loginId' value={userDetail.loginId || ""}
                            onChange={handleChange}
                        />
                        <Validator.Text />
                    </Validator>
                </Form.Group>
                <Form.Row>
                    <Form.Group as={Col} >
                        <Validator validation={Validation.firstName}>
                            <Form.Label >First name<Validator.Mand /></Form.Label>
                            <Form.Control
                                size='sm' type="string" placeholder='First name'
                                name='firstName' value={userDetail.firstName || ""}
                                onChange={handleChange}
                            />
                            <Validator.Text />
                        </Validator>
                    </Form.Group>
                    <Form.Group as={Col} >
                        <Form.Label >Last name</Form.Label>
                        <Form.Control
                            size='sm' type="string" placeholder='Last name'
                            name='lastName' value={userDetail.lastName || ""}
                            onChange={handleChange}
                        />
                        <Validator.Text />
                    </Form.Group>
                </Form.Row>
                <Form.Row>
                    <Form.Group as={Col} >
                        <Validator validation={Validation.roleId}>
                            <Form.Label>Role<Validator.Mand /></Form.Label>
                            <Form.Select name="roleId" className="select-control-sm" placeholder="Select role"
                                value={userDetail.roleId} onChange={handleSelect}
                                disabled={!isCreateMode && userDetail.roleId === QAPP_ROLE_ID ? true : false}
                                options={roleList.filter(item => {
                                    if (isCreateMode) return item.value !== QAPP_ROLE_ID;
                                    else return userDetail.roleId === QAPP_ROLE_ID ? true : item.value !== QAPP_ROLE_ID;
                                })}>
                            </Form.Select>
                            <Validator.Text />
                        </Validator>
                    </Form.Group>
                    <Form.Group as={Col} >
                        <Validator validation={Validation.statusId}>
                            <Form.Label>Status<Validator.Mand /></Form.Label>
                            <Form.Select name="statusId" autoComplete='off'
                                className="select-control-sm" placeholder="Select status"
                                value={userDetail.statusId} options={statusList} onChange={handleSelect}>
                            </Form.Select>
                            <Validator.Text />
                        </Validator>
                    </Form.Group>
                </Form.Row>
                <Form.Row>
                    <Form.Group as={Col}  >
                        <Validator validation={Validation.password}>
                            <Form.Label>Password{!isPasswordRequired() && <Validator.Mand />}</Form.Label>
                            <Form.Control
                                size='sm' type='password' placeholder='Password'
                                name='password' autoComplete="new-password"
                                value={userDetail.password || ""} onChange={handleChange}
                            />
                            <Validator.Text />
                        </Validator>
                    </Form.Group>
                    <Form.Group as={Col}  >
                        <Validator validation={Validation.confirmPassword}>
                            <Form.Label>Confirm password{!isPasswordRequired() && <Validator.Mand />}</Form.Label>
                            <Form.Control
                                size='sm' type='password' placeholder='Confirm password'
                                name='confirmPassword' autoComplete="new-password"
                                value={userDetail.confirmPassword || ""} onChange={handleChange}
                            />
                            <Validator.Text />
                        </Validator>
                    </Form.Group>
                </Form.Row>
                {/* <Form.Group  >
                    <Form.Label >Email</Form.Label>
                    <Form.Control
                        size='sm' autoComplete='off' type="email" placeholder='Email'
                        name='email' value={userDetail.email || ""}
                        onChange={handleChange}
                    />
                </Form.Group> */}
                <Form.Group >
                    <Validator validation={Validation.data}>
                        <Form.Label >Data (json)</Form.Label>
                        <Form.Control style={{ overflowY: "auto", height: "120px" }}
                            as='textarea' autoComplete='off' name='data' type='object'
                            value={userDetail.data || ""}
                            onChange={handleChange}
                        />
                        <Validator.Text />
                    </Validator>
                </Form.Group>
            </Form>
        </Modal.Body>
        <Modal.Footer>
            <Button variant='btn btn-danger btn-sm ' onClick={handleCancel} ><i className="fa fa-times-circle"></i> Cancel</Button>{' '}
            <Button variant='btn btn-primary btn-sm' onClick={handleSave}><Icon className="fa fa-floppy-o"></Icon>{isSaving ? " Saving..." : " Save"}</Button>
        </Modal.Footer>
    </Modal>
}

const mapStateToProps = (state, ownProps) => {
    const roleListById = state.users.roleListById.payload;
    return ({
        roleList: Object.keys(roleListById).map(roleId => ({ value: parseInt(roleId), label: roleListById[roleId].role_name })),
        statusList: state.users.statusList.map(e => ({ value: e.statusId, label: e.statusName })),
        userInfo: ownProps.userId ? state.users.userListById.payload[ownProps.userId] : null
    });
}

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({ createUser, editUser }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(UserForm);